import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ChevronLeft, Tag, Share2, MessageCircle, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
// Mock data for the post (in a real app, this would be fetched based on ID)
const postData = {
  id: 1,
  title: 'Transitioning to Assisted Living: How to Have Conversations with Aging Loved Ones',
  date: 'May 19, 2023',
  author: 'Admin',
  categories: ['Elderly Care', 'Health', 'Professionals'],
  image: 'https://elisabethhouse.net/wp-content/uploads/2023/05/pexels-kampus-production-7551583-scaled.jpg',
  content: ["Navigating the transition to assisted living is one of the most significant emotional journeys a family can take. It's not just about a change in address; it's about a change in lifestyle, autonomy, and the family dynamic.", "The first step is always the hardest: starting the conversation. Many seniors fear losing their independence, while family members fear for their loved one's safety. Bridging this gap requires empathy, patience, and a well-thought-out approach.", "Effective strategies for approaching the topic include choosing the right time and place—somewhere quiet and familiar where everyone feels comfortable. It's crucial to listen more than you speak. Ask open-ended questions like, 'How are you managing with the stairs lately?' or 'Do you ever feel lonely during the day?'", 'Address concerns and fears directly but gently. If they fear losing their social life, highlight the vibrant community activities at Elisabeth House. If they fear losing their privacy, discuss the private suites and personalized care plans that honor their dignity.', 'Involving your loved one in the decision-making process is vital. Take tours together, look at floor plans, and discuss the menu options. When seniors feel they are part of the choice rather than having a choice made for them, the transition becomes a shared journey toward a better quality of life.'],
  tags: ['Assisted Living', 'Elderly Care', 'Family Tips', 'Transition']
};
const relatedPosts = [{
  id: 2,
  title: "Assisted Living vs. Nursing Home: What's the Difference?",
  date: 'Dec 1, 2025',
  image: 'https://elisabethhouse.net/wp-content/uploads/2025/10/Untitled-9.791-x-8.208-in-1200-x-628-px-390x405.png'
}, {
  id: 4,
  title: "Living with Parkinson's: What Seniors and Families Should Know",
  date: 'Aug 28, 2025',
  image: 'https://elisabethhouse.net/wp-content/uploads/2025/08/istockphoto-1778181453-612x612-1-390x405.jpg'
}];
export const BlogDetails = () => {
  // In a real app, we'd use the ID from params
  // const { id } = useParams()
  return <div className="min-h-screen bg-white" data-id="element-361">
      {/* Page Header / Hero */}
      <section className="relative h-[60vh] min-h-[500px] flex items-end overflow-hidden pt-24 pb-16" data-id="element-362">
        <div className="absolute inset-0 z-0" data-id="element-363">
          <img src={postData.image} alt={postData.title} className="w-full h-full object-cover" data-id="element-364" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent" data-id="element-365" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 w-full" data-id="element-366">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-367">
            <div className="flex items-center space-x-3 text-white/60 text-xs uppercase tracking-[0.3em] mb-6" data-id="element-368">
              <Link to="/" className="hover:text-warmCoral transition-colors" data-id="element-369">
                Home
              </Link>
              <span className="w-1 h-1 bg-warmCoral rounded-full" data-id="element-370" />
              <Link to="/blog" className="hover:text-warmCoral transition-colors" data-id="element-371">
                Blog
              </Link>
              <span className="w-1 h-1 bg-warmCoral rounded-full" data-id="element-372" />
              <span className="text-white truncate max-w-[200px]" data-id="element-373">Details</span>
            </div>

            <div className="flex flex-wrap gap-2 mb-6" data-id="element-374">
              {postData.categories.map(cat => <span key={cat} className="bg-warmCoral text-white text-xs font-medium px-4 py-1 rounded-full uppercase tracking-wider" data-id="element-375">
                  {cat}
                </span>)}
            </div>

            <h1 className="text-white text-4xl md:text-6xl font-serif leading-tight mb-8" data-id="element-376">
              {postData.title}
            </h1>

            <div className="flex items-center space-x-8 text-white/80" data-id="element-377">
              <div className="flex items-center space-x-2" data-id="element-378">
                <Calendar className="w-5 h-5 text-warmCoral" data-id="element-379" />
                <span className="text-sm font-medium" data-id="element-380">{postData.date}</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-381">
                <User className="w-5 h-5 text-warmCoral" data-id="element-382" />
                <span className="text-sm font-medium" data-id="element-383">
                  By {postData.author}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20 px-4 md:px-8" data-id="element-384">
        <div className="max-w-4xl mx-auto" data-id="element-385">
          <div className="grid lg:grid-cols-12 gap-12" data-id="element-386">
            {/* Left Sidebar - Social Share (Desktop) */}
            <div className="hidden lg:block lg:col-span-1 sticky top-32 h-fit" data-id="element-387">
              <div className="flex flex-col space-y-6" data-id="element-388">
                <span className="text-[10px] uppercase tracking-[0.2em] text-charcoal/30 font-bold rotate-180 [writing-mode:vertical-lr]" data-id="element-389">
                  Share
                </span>
                <button className="p-3 rounded-full bg-warmGray/5 hover:bg-warmCoral hover:text-white text-charcoal/40 transition-all" data-id="element-390">
                  <Facebook className="w-5 h-5" data-id="element-391" />
                </button>
                <button className="p-3 rounded-full bg-warmGray/5 hover:bg-warmCoral hover:text-white text-charcoal/40 transition-all" data-id="element-392">
                  <Twitter className="w-5 h-5" data-id="element-393" />
                </button>
                <button className="p-3 rounded-full bg-warmGray/5 hover:bg-warmCoral hover:text-white text-charcoal/40 transition-all" data-id="element-394">
                  <Linkedin className="w-5 h-5" data-id="element-395" />
                </button>
              </div>
            </div>

            {/* Main Article Body */}
            <div className="lg:col-span-11" data-id="element-396">
              <div className="prose prose-lg max-w-none" data-id="element-397">
                {postData.content.map((para, i) => <p key={i} className="text-charcoal/70 text-xl leading-relaxed mb-8 font-light" data-id="element-398">
                    {para}
                  </p>)}
              </div>

              {/* Tags & Share (Mobile) */}
              <div className="mt-16 pt-12 border-t border-warmGray/10 flex flex-col md:flex-row md:items-center justify-between gap-8" data-id="element-399">
                <div className="flex flex-wrap gap-2" data-id="element-400">
                  {postData.tags.map(tag => <span key={tag} className="inline-flex items-center space-x-1 bg-warmGray/5 text-charcoal/50 px-4 py-2 rounded-full text-sm" data-id="element-401">
                      <Tag className="w-3 h-3" data-id="element-402" />
                      <span data-id="element-403">{tag}</span>
                    </span>)}
                </div>

                <div className="flex items-center space-x-4" data-id="element-404">
                  <span className="text-sm font-bold text-charcoal/30 uppercase tracking-widest" data-id="element-405">
                    Share:
                  </span>
                  <div className="flex space-x-2" data-id="element-406">
                    <button className="p-2 text-charcoal/40 hover:text-warmCoral transition-colors" data-id="element-407">
                      <Facebook className="w-5 h-5" data-id="element-408" />
                    </button>
                    <button className="p-2 text-charcoal/40 hover:text-warmCoral transition-colors" data-id="element-409">
                      <Twitter className="w-5 h-5" data-id="element-410" />
                    </button>
                    <button className="p-2 text-charcoal/40 hover:text-warmCoral transition-colors" data-id="element-411">
                      <Linkedin className="w-5 h-5" data-id="element-412" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Author Bio */}
              <div className="mt-16 bg-warmGray/5 rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center md:items-start gap-8" data-id="element-413">
                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 bg-warmGray/20" data-id="element-414">
                  <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200" alt="Admin" className="w-full h-full object-cover grayscale" data-id="element-415" />
                </div>
                <div data-id="element-416">
                  <h4 className="text-2xl font-serif text-charcoal mb-4" data-id="element-417">
                    About the Author
                  </h4>
                  <p className="text-charcoal/60 leading-relaxed mb-6" data-id="element-418">
                    Our administrative team is dedicated to providing the most
                    accurate and helpful information for families navigating the
                    complexities of senior care. With decades of combined
                    experience, we aim to be your trusted guide.
                  </p>
                  <div className="flex space-x-4" data-id="element-419">
                    <a href="#" className="text-warmCoral font-medium hover:underline" data-id="element-420">
                      View all posts
                    </a>
                  </div>
                </div>
              </div>

              {/* Related Posts */}
              <div className="mt-24" data-id="element-421">
                <h3 className="text-3xl font-serif text-charcoal mb-12" data-id="element-422">
                  Related Articles
                </h3>
                <div className="grid md:grid-cols-2 gap-8" data-id="element-423">
                  {relatedPosts.map(post => <Link key={post.id} to={`/blog/${post.id}`} className="group block" data-id="element-424">
                      <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-6" data-id="element-425">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-426" />
                      </div>
                      <span className="text-warmCoral text-xs font-bold uppercase tracking-widest mb-3 block" data-id="element-427">
                        {post.date}
                      </span>
                      <h4 className="text-xl font-serif text-charcoal group-hover:text-warmCoral transition-colors leading-snug" data-id="element-428">
                        {post.title}
                      </h4>
                    </Link>)}
                </div>
              </div>

              {/* Back to Blog */}
              <div className="mt-20 text-center" data-id="element-429">
                <Link to="/blog" className="inline-flex items-center space-x-2 text-charcoal font-medium hover:text-warmCoral transition-colors group" data-id="element-430">
                  <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-431" />
                  <span data-id="element-432">Back to all articles</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
};